import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { baserowService } from '@/services/baserow'
import { useAppStore } from './app'
import { getUserPermissions, hasPermission, type UserRole, type Permission } from '@/utils/permissions'

export interface User {
  id: string
  email: string
  name: string
  role?: string
  active: boolean
  created_at?: string
  last_login?: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  name: string
  email: string
  password: string
}

export const useAuthStore = defineStore('auth', () => {
  const appStore = useAppStore()
  
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'Team')
  const userRole = computed(() => user.value?.role as UserRole)
  const permissions = computed(() => getUserPermissions(userRole.value))

  // Initialize from localStorage
  const initAuth = () => {
    const savedToken = localStorage.getItem('breizhteam_auth_token')
    const savedUser = localStorage.getItem('breizhteam_auth_user')
    
    if (savedToken && savedUser) {
      token.value = savedToken
      user.value = JSON.parse(savedUser)
    }
  }

  // Save to localStorage
  const saveAuth = (authToken: string, userData: User) => {
    token.value = authToken
    user.value = userData
    localStorage.setItem('breizhteam_auth_token', authToken)
    localStorage.setItem('breizhteam_auth_user', JSON.stringify(userData))
  }

  // Clear auth
  const clearAuth = () => {
    token.value = null
    user.value = null
    localStorage.removeItem('breizhteam_auth_token')
    localStorage.removeItem('breizhteam_auth_user')
  }

  // Login
  const login = async (credentials: LoginCredentials) => {
    try {
      appStore.setLoading(true)
      
      const response = await baserowService.login(credentials)
      
      if (response.token && response.user) {
        saveAuth(response.token, response.user)
        appStore.showSuccess('Connexion réussie!')
        return true
      }
      
      return false
    } catch (error: any) {
      appStore.showError(error.message || 'Erreur de connexion')
      return false
    } finally {
      appStore.setLoading(false)
    }
  }

  // Register
  const register = async (data: RegisterData) => {
    try {
      appStore.setLoading(true)
      
      const response = await baserowService.register(data)
      
      if (response.token && response.user) {
        saveAuth(response.token, response.user)
        appStore.showSuccess('Inscription réussie!')
        return true
      }
      
      return false
    } catch (error: any) {
      appStore.showError(error.message || 'Erreur d\'inscription')
      return false
    } finally {
      appStore.setLoading(false)
    }
  }

  // Logout
  const logout = () => {
    clearAuth()
    appStore.showInfo('Déconnexion réussie')
  }

  // Check auth status
  const checkAuthStatus = async () => {
    if (!token.value) return false
    
    try {
      const response = await baserowService.verifyToken(token.value)
      
      if (response.valid && response.user) {
        user.value = response.user
        return true
      } else {
        clearAuth()
        return false
      }
    } catch (error) {
      clearAuth()
      return false
    }
  }

  // Login with Google
  const loginWithGoogle = async (userData: User, accessToken: string) => {
    try {
      appStore.setLoading(true)
      
      // Here you would typically call your backend to verify the Google token
      // and create/retrieve the user. For now, we'll simulate success.
      
      // Create a session token (in real app, this comes from your backend)
      const sessionToken = `google_session_${Date.now()}_${userData.id}`
      
      saveAuth(sessionToken, userData)
      appStore.showSuccess('Connexion Google réussie!')
      
      return true
    } catch (error: any) {
      appStore.showError(error.message || 'Erreur de connexion Google')
      return false
    } finally {
      appStore.setLoading(false)
    }
  }

  // Update profile
  const updateProfile = async (userData: Partial<User>) => {
    if (!user.value) return false
    
    try {
      appStore.setLoading(true)
      
      const updatedUser = await baserowService.updateUser(user.value.id, userData)
      
      if (updatedUser) {
        user.value = { ...user.value, ...updatedUser }
        saveAuth(token.value!, user.value)
        appStore.showSuccess('Profil mis à jour!')
        return true
      }
      
      return false
    } catch (error: any) {
      appStore.showError(error.message || 'Erreur de mise à jour')
      return false
    } finally {
      appStore.setLoading(false)
    }
  }

  return {
    user,
    token,
    isAuthenticated,
    isAdmin,
    userRole,
    permissions,
    initAuth,
    login,
    loginWithGoogle,
    register,
    logout,
    checkAuthStatus,
    updateProfile,
    saveAuth,
    hasPermission: (permission: keyof Permission) => hasPermission(userRole.value, permission)
  }
})